import { DashboardCard } from "@dashboard/components/Card";
import CardSpacer from "@dashboard/components/CardSpacer";
import { OrderErrorFragment, OrderLineFragment, OrderLineInput } from "@dashboard/graphql";
import { Box, Button } from "@saleor/macaw-ui-next";
import { FormattedMessage } from "react-intl";

import OrderCardTitle from "../OrderCardTitle";
import { OrderDetailsDatagrid } from "../OrderDetailsDatagrid";
import { OrderDraftDetailsDatagrid } from "../OrderDraftDetailsDatagrid/OrderDraftDetailsDatagrid";
import { useStyles } from "./styles";
import {
  getLineQuantityForUnfulfilledQuantity,
  toEditableUnfulfilledLine,
  toLineWithUnfulfilledQuantity,
} from "./utils";

interface OrderUnfulfilledProductsCardEditing {
  errors: OrderErrorFragment[];
  onOrderLineAdd: () => void;
  onOrderLineChange: (id: string, data: OrderLineInput) => void;
  onOrderLineRemove: (id: string) => void;
}

interface OrderUnfulfilledProductsCardProps {
  lines: OrderLineFragment[];
  loading: boolean;
  onShowMetadata: (id: string) => void;
  // Set when the user may add/change/remove lines of this placed order.
  editing?: OrderUnfulfilledProductsCardEditing;
  // Offer the per-line "Edit price / discount" action in the read-only grid.
  canEditLineDiscount?: boolean;
}

// The "Fulfill" action now lives in the order progress stepper so all
// order-progression controls sit in one guided place.
const OrderUnfulfilledProductsCard = ({
  onShowMetadata,
  lines,
  loading,
  editing,
  canEditLineDiscount,
}: OrderUnfulfilledProductsCardProps) => {
  const classes = useStyles();

  if (!lines.length && !editing) {
    return null;
  }

  // Quantities in the editable grid are the unfulfilled remainder; translate
  // them back to the line's total quantity. Removing a partially fulfilled
  // line only drops its unfulfilled part.
  const handleLineChange = (id: string, { quantity }: { quantity: number }) => {
    const line = lines.find(line => line.id === id);

    if (!line) {
      return;
    }

    const newQuantity = getLineQuantityForUnfulfilledQuantity(line, Number(quantity));

    if (newQuantity <= 0) {
      editing?.onOrderLineRemove(id);
    } else {
      editing?.onOrderLineChange(id, { quantity: newQuantity });
    }
  };
  const handleLineRemove = (id: string) => {
    const line = lines.find(line => line.id === id);

    if (line && line.quantityFulfilled > 0) {
      handleLineChange(id, { quantity: 0 });
    } else {
      editing?.onOrderLineRemove(id);
    }
  };

  return (
    <>
      <DashboardCard>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <OrderCardTitle withStatus status="unfulfilled" className={classes.cardTitle} />
          {editing && (
            <Box paddingRight={6}>
              <Button
                variant="secondary"
                onClick={editing.onOrderLineAdd}
                data-test-id="add-products-button"
              >
                <FormattedMessage id="C50ahv" defaultMessage="Add products" description="button" />
              </Button>
            </Box>
          )}
        </Box>
        <DashboardCard.Content paddingX={0}>
          {editing ? (
            <OrderDraftDetailsDatagrid
              lines={lines.map(toEditableUnfulfilledLine)}
              loading={loading}
              errors={editing.errors.filter(error => error.field === "lines")}
              onOrderLineChange={handleLineChange}
              onOrderLineRemove={handleLineRemove}
              onShowMetadata={onShowMetadata}
            />
          ) : (
            <OrderDetailsDatagrid
              lines={toLineWithUnfulfilledQuantity(lines)}
              loading={loading}
              onShowMetadata={onShowMetadata}
              canEditLineDiscount={canEditLineDiscount}
            />
          )}
        </DashboardCard.Content>
      </DashboardCard>
      <CardSpacer />
    </>
  );
};

export default OrderUnfulfilledProductsCard;
